import { Children, useEffect, useId, useRef, type ReactNode } from 'react'
import { useEditing } from './editing'

/**
 * What a control is called, for the settle window.
 *
 * A plain string, and per-person controls carry the person in it. The
 * primitives never read it: they hand it to `editing` so a value being typed is
 * withheld from the write until it settles.
 */
type FactField = string

/**
 * Groups related questions. Question IDs are audit identifiers tied to the rule
 * set, not a display sequence. Sections are what make the order read as
 * deliberate rather than as a gap in the numbering.
 */
export function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: ReactNode
}) {
  const count = Children.count(children)
  return (
    <section className="section">
      <header className="section-head">
        <h2>{title}</h2>
        <span className="section-count">
          {count} {count === 1 ? 'question' : 'questions'}
        </span>
        {hint && <p>{hint}</p>}
      </header>
      {children}
    </section>
  )
}

/**
 * Questions carry no visible identifier. The Q-numbers are audit identifiers
 * tied to the rule set. They belong in the result trace, where someone is
 * checking why a status came out, not in front of the person answering.
 */
export function Question({
  title,
  hint,
  children,
}: {
  title: string
  hint?: ReactNode
  children: ReactNode
}) {
  return (
    <fieldset className="question">
      <legend>{title}</legend>
      {hint && <p className="hint">{hint}</p>}
      {children}
    </fieldset>
  )
}

export function RadioGroup<T extends string>({
  name,
  options,
  value,
  onChange,
}: {
  name: string
  options: { value: T; label: string; hint?: string }[]
  value: T | null
  onChange: (v: T) => void
}) {
  return (
    <div className="options">
      {options.map((o) => (
        <label key={o.value} className={value === o.value ? 'option selected' : 'option'}>
          <input
            type="radio"
            name={name}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
          />
          <span>
            {o.label}
            {o.hint && <small>{o.hint}</small>}
          </span>
        </label>
      ))}
    </div>
  )
}

export function CheckboxGroup<T extends string>({
  options,
  value,
  onChange,
  exclusive = [],
  grid = false,
}: {
  options: { value: T; label: string; hint?: string }[]
  value: T[]
  onChange: (v: T[]) => void
  /** Values that clear every other selection when picked, e.g. NONE / UNKNOWN. */
  exclusive?: T[]
  /**
   * Laid out across rather than down, for a closed set of short labels.
   *
   * The same trade TileGroup makes and for the same reason: a set worth seeing
   * in full should be seeable in full. A column of twenty place names is a
   * scroll, and the answer is somewhere in it; a grid of twenty is read at
   * once. Only where the labels are short, which rules out the option sets
   * written as sentences.
   */
  grid?: boolean
}) {
  const toggle = (v: T) => {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v))
      return
    }
    if (exclusive.includes(v)) {
      onChange([v])
      return
    }
    onChange([...value.filter((x) => !exclusive.includes(x)), v])
  }

  return (
    <div className={grid ? 'options options-grid' : 'options'}>
      {options.map((o) => (
        <label key={o.value} className={value.includes(o.value) ? 'option selected' : 'option'}>
          <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />
          <span>
            {o.label}
            {o.hint && <small>{o.hint}</small>}
          </span>
        </label>
      ))}
    </div>
  )
}

export function NumberField({
  value,
  onChange,
  prefix,
  placeholder,
  min = 0,
  field,
}: {
  value: number | null
  onChange: (v: number | null) => void
  prefix?: string
  placeholder?: string
  min?: number
  /**
   * Which answer this writes, so the record can wait until it is finished.
   *
   * Optional: a control editing something inside another answer has no fact
   * field of its own, and a half-entered figure there is held back by the
   * answer it belongs to or not at all. See editing.ts.
   */
  field?: FactField
}) {
  const editing = useEditing()
  return (
    <div className="number-field">
      {prefix && <span className="prefix">{prefix}</span>}
      <input
        type="number"
        min={min}
        inputMode="decimal"
        placeholder={placeholder}
        value={value === null ? '' : value}
        onFocus={field ? () => editing.begin(field) : undefined}
        onBlur={field ? () => editing.end(field) : undefined}
        onChange={(e) => {
          const raw = e.target.value
          onChange(raw === '' ? null : Number(raw))
        }}
      />
    </div>
  )
}

export function TextField({
  value,
  onChange,
  placeholder,
  id,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  id?: string
}) {
  return (
    <input
      id={id}
      className="text-field"
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/**
 * A list of short strings, each typed rather than picked.
 *
 * For identifiers: a registration number is written the same way by the
 * business and by the schedule, so the two lists can be compared. Not for
 * anything a person would phrase in their own words, because two phrasings of
 * one thing cannot be compared at all.
 *
 * Blank rows are dropped on the way out, so the stored list never carries an
 * empty entry that would read as an item the schedule names.
 */
export function StringListField({
  value,
  onChange,
  placeholder,
  addLabel,
}: {
  value: string[]
  onChange: (v: string[]) => void
  placeholder?: string
  addLabel: string
}) {
  const clean = (rows: string[]) => onChange(rows.map((r) => r.trim()).filter(Boolean))

  // One empty row to type into, which is not part of the value until it has
  // something in it.
  const rows = [...value, '']

  return (
    <div className="string-list">
      {rows.map((row, i) => (
        <div key={i} className="string-list-row">
          <TextField
            value={row}
            onChange={(v) => clean(rows.map((r, j) => (i === j ? v : r)))}
            placeholder={placeholder}
          />
          {i < value.length && (
            <button
              type="button"
              className="policy-drop"
              onClick={() => clean(value.filter((_, j) => j !== i))}
              aria-label={`Remove ${value[i]}`}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      <p className="hint">{addLabel}</p>
    </div>
  )
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  allowEmpty = true,
}: {
  value: T | null
  onChange: (v: T | null) => void
  options: { value: T; label: string }[]
  placeholder?: string
  allowEmpty?: boolean
}) {
  return (
    <select
      className="select"
      value={value ?? ''}
      onChange={(e) => onChange((e.target.value || null) as T | null)}
    >
      {allowEmpty && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

/**
 * All three of these wrap a native radio group. Styling a <button> and adding
 * role="radio" promises keyboard behaviour it does not deliver: real radios
 * give arrow-key navigation, a single tab stop per group, screen reader
 * position announcements and form semantics for nothing.
 *
 * A small, mutually exclusive set of short answers. Three stacked radio rows
 * for yes/no/not-known is a lot of vertical space for one bit of information;
 * a segmented control says the same thing in one row and reads as a single
 * decision rather than three separate ones.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[]
  value: T | null
  onChange: (v: T) => void
}) {
  const name = useId()
  return (
    <div className="segmented">
      {options.map((o) => (
        <label key={o.value} className="seg">
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
          />
          {o.label}
        </label>
      ))}
    </div>
  )
}

/**
 * One choice from a known, closed set worth seeing all at once. A dropdown
 * hides the options, which is the wrong trade when the list is the answer
 * space and the user may not know what the categories are.
 */
export function TileGroup<T extends string>({
  options,
  value,
  onChange,
  allowClear = false,
  disabled = false,
}: {
  options: { value: T; label: string }[]
  value: T | null
  onChange: (v: T | null) => void
  allowClear?: boolean
  disabled?: boolean
}) {
  const name = useId()
  return (
    <div className="tiles">
      {options.map((o) => {
        const selected = value === o.value
        return (
          <label key={o.value} className="tile-opt">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={selected}
              disabled={disabled}
              onChange={() => onChange(o.value)}
              // Click fires on an already-checked radio where change does not,
              // which is what lets an optional choice be unset.
              onClick={() => {
                if (selected && allowClear) onChange(null)
              }}
            />
            {o.label}
          </label>
        )
      })}
    </div>
  )
}

/** A handful of short, ordered choices. Wider than a segment, still one line. */
export function ChoiceChips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[]
  value: T | null
  onChange: (v: T) => void
}) {
  const name = useId()
  return (
    <div className="choice-chips">
      {options.map((o) => (
        <label key={o.value} className="choice">
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
          />
          {o.label}
        </label>
      ))}
    </div>
  )
}

/**
 * Money. Grouped in thousands as you type, because a bare seven-digit field is
 * where people misplace a zero. `note` carries live feedback, e.g. the band a
 * figure falls into, so the user can sanity-check without doing the arithmetic.
 */
export function MoneyField({
  value,
  onChange,
  placeholder,
  note,
  field,
}: {
  value: number | null
  onChange: (v: number | null) => void
  placeholder?: string
  note?: string
  /** Which answer this writes, so the record can wait. See editing.ts. */
  field?: FactField
}) {
  const editing = useEditing()
  return (
    <div className="money">
      <div className="money-input">
        <span className="prefix">S$</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholder}
          value={value === null ? '' : value.toLocaleString('en-SG')}
          onFocus={field ? () => editing.begin(field) : undefined}
          onBlur={field ? () => editing.end(field) : undefined}
          onChange={(e) => {
            const digits = e.target.value.replace(/[^\d]/g, '')
            onChange(digits === '' ? null : Number(digits))
          }}
        />
      </div>
      {note && <span className="money-note">{note}</span>}
    </div>
  )
}

/** Month and year. Precise enough for an incident, and not more than is known. */
export function MonthField({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  // Accepts what is already stored, including free text from earlier answers.
  const asMonth = /^\d{4}-\d{2}$/.test(value)
    ? value
    : (() => {
        const parsed = new Date(`1 ${value}`)
        return Number.isNaN(parsed.getTime())
          ? ''
          : `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`
      })()

  return (
    <div className="month">
      <input
        type="month"
        className="text-field"
        value={asMonth}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && !asMonth && <span className="money-note">Recorded as “{value}”</span>}
    </div>
  )
}

/**
 * Something irreversible, asked in the app's own voice.
 *
 * This was `window.confirm`, which is the one control a browser insists on
 * drawing itself: system chrome, the origin printed above the question, and a
 * single line with no room to say what is about to be lost. Deleting somebody's
 * screening is worth a sentence naming what goes.
 *
 * Built on the same <dialog> as the answering sheet, so the backdrop, the focus
 * trap, Esc and returning focus to whatever opened it all come for free rather
 * than being reimplemented.
 *
 * Sized and worded to match `ConfirmContext` in asvise/shared: the destructive
 * button is the filled red one, cancel is the outline beside it, and Enter
 * takes the action the same way it does there. That app cannot be imported here
 * (React 19, Tailwind, a router), so the agreement has to be kept by hand.
 */
export function Confirm({
  title,
  detail,
  action,
  destructive = true,
  onConfirm,
  onClose,
}: {
  title: string
  detail?: string
  /** Names what happens, so the button is not a bare "OK". */
  action: string
  /**
   * Whether the thing being confirmed actually destroys something.
   *
   * Destructive by default, because that is what a confirmation was for here
   * until signing out started asking too. Signing out is worth a pause, since
   * the device forgets the screening and getting back in means finding a
   * password, but it loses nothing, and a red button over the words "nothing is
   * lost" contradicts them. A dialog that overstates every time is one people
   * stop reading.
   */
  destructive?: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    ref.current?.showModal()
  }, [])

  const close = () => ref.current?.close()

  return (
    <dialog
      ref={ref}
      className="sheet"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) close()
      }}
    >
      {/* A form with method="dialog" is what makes Enter take the action, since
          the submit button inside it is the dialog's default. Doing it this way
          rather than on a key listener keeps Esc, Enter and the buttons all on
          the same path out. */}
      <form method="dialog" className="sheet-body confirm">
        <h2>{title}</h2>
        {detail && <p>{detail}</p>}
        <footer className="confirm-foot">
          <button type="button" className="secondary" onClick={close}>
            Cancel
          </button>
          <button autoFocus className={destructive ? 'danger' : 'primary'} onClick={onConfirm}>
            {action}
          </button>
        </footer>
      </form>
    </dialog>
  )
}

/**
 * A view reached from the board.
 *
 * The board is the page; these are ways of looking at it more closely, so every
 * one of them opens the same way and leaves the same way. Back sits above the
 * heading rather than at the foot, because two of these scroll and a way out
 * that has to be scrolled to is not a way out.
 */
export function SecondaryView({
  title,
  description,
  onBack,
  children,
}: {
  title: string
  description?: ReactNode
  onBack: () => void
  children: ReactNode
}) {
  return (
    <section className="secondary">
      <button className="back" onClick={onBack}>
        <span aria-hidden="true">←</span> Back to the picture
      </button>

      <header className="secondary-head">
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </header>

      {children}
    </section>
  )
}
